import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue } from "framer-motion";

// PHOTO CLIENT — hôtel partenaire, patio & piscine
const HERO_IMG = "/images/hotel-piscine.png";

const lineAnim = (i) => ({
  initial: { y: "115%" },
  animate: { y: 0 },
  transition: { duration: 1.15, delay: 0.35 + i * 0.13, ease: [0.22, 1, 0.36, 1] },
});

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yOrbs = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const yPhoto = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const orbX = useTransform(mx, [-0.5, 0.5], [-36, 36]);
  const photoX = useTransform(mx, [-0.5, 0.5], [12, -12]);

  const handleMouse = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  const go = (e, href) => {
    e.preventDefault();
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section ref={ref} id="top" onMouseMove={handleMouse} className="relative min-h-screen flex items-center overflow-hidden pt-32 pb-24 md:pb-32">
      {/* Orbes colorés — signature visuelle */}
      <motion.div style={{ y: yOrbs, x: orbX }} className="absolute inset-0" aria-hidden="true">
        <div className="orb w-[520px] h-[520px] lg:w-[760px] lg:h-[760px] bg-[#FF4B3E] blur-[130px] opacity-50 -top-40 right-[-10%]" />
        <div className="orb w-[420px] h-[420px] bg-[#FF2A7A] blur-[120px] opacity-35 top-[35%] right-[22%]" />
        <div className="orb w-[380px] h-[380px] bg-[#FFB347] blur-[110px] opacity-45 bottom-[-10%] left-[-6%]" />
      </motion.div>

      <div className="wrap relative z-10 grid lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-7">
          <h1 className="display-hero text-[clamp(3rem,8.5vw,7.2rem)]" data-testid="hero-title">
            <span className="block overflow-hidden pb-1">
              <motion.span className="block" {...lineAnim(0)}>Vos temps creux</motion.span>
            </span>
            <span className="block overflow-hidden pb-1">
              <motion.span className="block" {...lineAnim(1)}>
                sont <em className="font-serifit italic font-normal normal-case">nos</em>
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span className="block font-script text-[#E8431F] text-[1.15em] leading-[1.05]" {...lineAnim(2)}>
                temps plein.
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.0 }}
            className="mt-7"
          >
            <span data-testid="hero-no-fees" className="font-serifit italic text-lg md:text-xl text-[#5F5258]">
              Sans frais engagés
            </span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.95 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <a href="#etablissements" onClick={(e) => go(e, "#etablissements")} data-testid="hero-cta-lieux" className="btn btn-ink">
              Lieux & établissements
            </a>
            <a href="#pros" onClick={(e) => go(e, "#pros")} data-testid="hero-cta-pros" className="btn glass text-[#1C0E14]">
              Pros du bien-être
            </a>
            <a href="#agence" onClick={(e) => go(e, "#agence")} data-testid="hero-cta-agences" className="btn glass text-[#1C0E14]">
              Agences
            </a>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, y: 60, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <motion.figure
              style={{ y: yPhoto, x: photoX }}
              data-testid="hero-photo"
              className="relative overflow-hidden rounded-[32px] aspect-[3/4] max-w-[440px] ml-auto shadow-[0_50px_100px_-40px_rgba(28,14,20,0.45)]"
            >
              <img src={HERO_IMG} alt="Hôtel partenaire, patio et piscine" className="h-full w-full object-cover" />
            </motion.figure>

            {/* Badge rotatif */}
            <motion.div
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, delay: 1.35 }}
              data-testid="hero-badge"
              className="absolute -top-8 -right-2 md:right-6 w-[110px] h-[110px]"
            >
              <svg viewBox="0 0 100 100" className="spin-slow w-full h-full">
                <path id="dpCircle" d="M 50,50 m -38,0 a 38,38 0 1,1 76,0 a 38,38 0 1,1 -76,0" fill="none" />
                <text className="font-mono2" style={{ fontSize: "8.2px", letterSpacing: "2.4px", fill: "#1C0E14" }}>
                  <textPath href="#dpCircle">DOUCEUR POWER · STUDIO WELLNESS · PROD ·</textPath>
                </text>
              </svg>
              <span className="absolute inset-0 flex items-center justify-center font-serifit italic text-xl text-[#FF4B3E]">
                DP.
              </span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
